import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { MfaService } from '../../../services/mfa.service';
import { AuthService } from '../../../services/auth.service';
import { MfaSetupState } from '../../../models/mfa.model';

@Component({
  selector: 'app-mfa-setup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './mfa-setup.component.html',
  styleUrls: ['./mfa-setup.component.scss']
})
export class MfaSetupComponent implements OnInit, OnDestroy {
  @Output() setupCompleted = new EventEmitter<boolean>();
  @Output() setupCancelled = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  verificationForm: FormGroup;
  setupState: MfaSetupState = { step: 'init' };
  loading = false;
  error = '';

  constructor(
    private fb: FormBuilder,
    private mfaService: MfaService,
    private authService: AuthService,
    private router: Router
  ) {
    this.verificationForm = this.fb.group({
      verificationCode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    this.mfaService.setupState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.setupState = state;
      });

    this.initializeMfaSetup();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeMfaSetup() {
    this.loading = true;
    this.error = '';

    const userInfo = this.authService.getUserInfo();

    if (!userInfo.username) {
      this.error = '無法取得使用者資訊，請重新登入';
      this.loading = false;
      return;
    }


    this.mfaService.initSetup(userInfo.username)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.setupToken) {
            this.mfaService.updateSetupState({
              step: 'qrcode',
              setupToken: response.setupToken
            });
            this.loadQrCode(response.setupToken);
          } else {
            this.error = response.message || 'MFA 初始化失敗';
          }
        },
        error: (error) => {
          console.error('MFA 初始化錯誤:', error);
          this.error = '初始化 MFA 時發生錯誤，請稍後再試';
        }
      });
  }

  loadQrCode(setupToken: string) {
    this.loading = true;
    this.error = '';

    this.mfaService.getQrCode(setupToken)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.mfaService.updateSetupState({
              step: 'verify',
              setupToken: setupToken,
              qrCodeImage: response.qrCodeImage,
              issuer: response.issuer,
              accountName: response.accountName
            });
          } else {
            this.error = response.message || '無法載入 QR Code';
          }
        },
        error: (error) => {
          console.error('載入 QR Code 錯誤:', error);
          this.error = '載入 QR Code 時發生錯誤，請稍後再試';
        }
      });
  }

  onVerify() {
    if (this.verificationForm.invalid) {
      return;
    }

    const verificationCode = this.verificationForm.get('verificationCode')?.value;
    const setupToken = this.setupState.setupToken;

    if (!setupToken) {
      this.error = '設定過程中發生錯誤，請重新開始';
      return;
    }

    this.loading = true;
    this.error = '';

    this.mfaService.completeSetup(setupToken, verificationCode)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.mfaService.updateSetupState({
              step: 'complete',
              setupToken: setupToken,
              backupCodes: response.backupCodes
            });

            // Update MFA status
            this.mfaService.updateMfaStatus({
              isEnabled: true,
              hasBackupCodes: true,
              setupDate: new Date().toISOString()
            });
          } else {
            this.error = response.message || '驗證碼錯誤，請重試';
          }
        },
        error: (error) => {
          console.error('MFA 設定完成錯誤:', error);
          this.error = '設定過程中發生錯誤，請稍後再試';
        }
      });
  }

  onComplete() {
    // Reset setup state and emit completion event
    this.mfaService.resetSetupState();
    this.setupCompleted.emit(true);
  }

  onCancel() {
    // Reset setup state and emit cancel event
    this.mfaService.resetSetupState();
    this.setupCancelled.emit();
  }

  onRestart() {
    this.mfaService.resetSetupState();
    this.verificationForm.reset();
    this.initializeMfaSetup();
  }

  get isInitStep() { return this.setupState.step === 'init'; }
  get isQrCodeStep() { return this.setupState.step === 'qrcode'; }
  get isVerifyStep() { return this.setupState.step === 'verify'; }
  get isCompleteStep() { return this.setupState.step === 'complete'; }
}
